import { Settings01Icon } from '@hugeicons/core-free-icons';
import { FormTypes } from 'constants/forms';
import { Permissions } from 'constants/permissions';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import {
  Button,
  InputGroup,
  RichTextEditor,
  Select,
  Textarea,
  ToggleSwitchInput,
} from 'components/FormComponents';

/**
 * Form-level settings in a modal: Team, Description (candidate), Has intro statement (candidate).
 * On Save, calls onSave() so the parent can persist the form.
 */
export const FormSettings = ({
  visible,
  onClose,
  form,
  teams = [],
  register,
  control,
  watch,
  setValue,
  hasAccess = () => true,
  hasIntroField = false,
  onHasIntroFieldChange = () => {},
  onSave = () => {},
}) => {
  const watchTeam = watch?.('team_id');
  const isCandidate = form?.type === FormTypes.CANDIDATE || form?.type === FormTypes.QUESTIONNAIRE;
  const showTeamSelect = hasAccess(Permissions.TeamsRead);

  const handleSave = () => {
    onSave();
    onClose();
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title="Form settings"
      icon={Settings01Icon}
      oldIconLibrary={false}
      small>
      <div className="flex flex-col">
        {showTeamSelect && (
          <InputGroup title="Team">
            <Select
              name="team_id"
              placeholder="Team"
              register={register}
              value={watchTeam}
              useDefault={!showTeamSelect}>
              <Select.Item value={null}>Global</Select.Item>
              {teams.map(team => (
                <Select.Item key={team.id} value={team.id}>
                  {team.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        )}

        <InputGroup
          title="Description"
          description="This breif summary which is displayed on the form overview page for your reference.">
          <Textarea
            name="description"
            register={register}
            placeholder="Description..."
            inputProps={{ rows: 3 }}
          />
        </InputGroup>

        <InputGroup
          title="Message"
          description={`This message will be shown to the ${isCandidate ? 'candidate' : 'referee'} on their experience.`}>
          <Controller
            name="message"
            control={control}
            render={({ field: { value, onChange } }) => (
              <RichTextEditor value={value} onChange={onChange} />
            )}
          />
        </InputGroup>

        {isCandidate && (
          <>
            <ToggleSwitchInput
              text="Has Introduction Statement Field"
              noMaxWidth
              subtext="Enable this to add an introductory statement at the start of your form. This field appears before all other questions and cannot be reordered, duplicated, or have logic applied. You can delete it and re-enable it anytime by toggling this switch."
              onChange={e => onHasIntroFieldChange(e.target.checked)}
              value={hasIntroField}
            />
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="secondary-reverse" onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </AnimatedModal>
  );
};

FormSettings.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  form: PropTypes.shape({
    type: PropTypes.string,
  }),
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  ),
  register: PropTypes.func.isRequired,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  hasAccess: PropTypes.func,
  hasIntroField: PropTypes.bool,
  onHasIntroFieldChange: PropTypes.func,
  onSave: PropTypes.func,
};
